import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd'
import { accountsApi, operatorsApi } from '../services/api'
import { featuresApi } from '../services/privacyApi'
import {
    visibilityProfilesApi,
    type VisibilityProfile,
    type VisibilityProfilePayload,
} from '../services/visibilityProfilesApi'

const { Text } = Typography

interface AccountOption {
    id: number
    phone?: string | null
    username?: string | null
    status?: string | null
    pool_temperature?: string | null
}

// Цвет метки аккаунта - по его пулу.
const TEMPERATURE_COLOR: Record<string, string> = { warm: 'orange', hot: 'red' }

interface OperatorOption {
    id: number
    username: string
    full_name?: string | null
    is_active: boolean
}

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

// Тумблер со спросом вплотную к нему - как в настройках интеграции.
function ConfirmSwitch({ checked, what, onConfirm }: { checked: boolean; what: string; onConfirm: (next: boolean) => void }) {
    const [open, setOpen] = useState(false)
    const next = !checked
    return (
        <Popconfirm
            open={open}
            onOpenChange={setOpen}
            placement="topRight"
            title={next ? 'Включить?' : 'Выключить?'}
            description={`Вы уверены, что хотите ${next ? 'включить' : 'выключить'} «${what}»?`}
            okText={next ? 'Да, включить' : 'Да, выключить'}
            cancelText="Отмена"
            onConfirm={() => { setOpen(false); onConfirm(next) }}
            onCancel={() => setOpen(false)}
        >
            <Switch checked={checked} />
        </Popconfirm>
    )
}

// Подраздел «Профили видимости»: команды, их почта Wiseway и сотрудники,
// переключатель видимости по профилям и разовый переход на профили.
export default function VisibilityProfilesSettings({ onChanged }: { onChanged?: () => void } = {}) {
    const [profiles, setProfiles] = useState<VisibilityProfile[]>([])
    const [operators, setOperators] = useState<OperatorOption[]>([])
    const [accounts, setAccounts] = useState<AccountOption[]>([])
    const [enabled, setEnabled] = useState(false)
    const [loading, setLoading] = useState(true)
    const [editing, setEditing] = useState<VisibilityProfile | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [form] = Form.useForm()

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const [profilesResponse, operatorsResponse, featuresResponse, accountsResponse] = await Promise.all([
                visibilityProfilesApi.list(),
                operatorsApi.getAll(0, 500),
                featuresApi.getSettings(),
                accountsApi.getAll(0, 1000, true),
            ])
            setProfiles(profilesResponse.data)
            setOperators(operatorsResponse.data as OperatorOption[])
            setAccounts((accountsResponse.data as AccountOption[]).filter((account) => account.status !== 'deleted'))
            setEnabled(Boolean(featuresResponse.data.profile_visibility_enabled))
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const operatorOptions = useMemo(() => operators.map((operator) => ({
        value: operator.id,
        label: `${operator.full_name || operator.username}${operator.is_active ? '' : ' (неактивен)'}`,
    })), [operators])

    // Аккаунты нужного пула; прикреплённые к другому профилю видны, но недоступны.
    const accountOptions = useCallback((temperature: 'warm' | 'hot') => {
        const takenBy = new Map<number, string>()
        profiles.forEach((profile) => {
            if (profile.id !== editing?.id) profile.account_ids.forEach((id) => takenBy.set(id, profile.name))
        })
        return accounts
            .filter((account) => account.pool_temperature === temperature)
            .map((account) => {
                const taken = takenBy.get(account.id)
                const name = account.username ? `@${account.username}` : (account.phone || `#${account.id}`)
                return {
                    value: account.id,
                    label: taken ? `${name} — уже в профиле «${taken}»` : name,
                    disabled: Boolean(taken),
                }
            })
    }, [accounts, profiles, editing])

    const toggleEnabled = async (next: boolean) => {
        setEnabled(next)
        try {
            await featuresApi.updateSettings({ profile_visibility_enabled: next })
            message.success(next ? 'Видимость по профилям включена' : 'Видимость по профилям выключена')
        } catch (error) {
            setEnabled(!next)
            message.error(extractError(error))
        }
    }

    const openCreate = () => {
        setEditing(null)
        form.resetFields()
        form.setFieldsValue({ is_active: true, member_ids: [], warm_account_ids: [], hot_account_ids: [] })
        setModalOpen(true)
    }

    const openEdit = (profile: VisibilityProfile) => {
        setEditing(profile)
        form.setFieldsValue({
            name: profile.name,
            wiseway_email: profile.wiseway_email,
            description: profile.description || '',
            is_active: profile.is_active,
            member_ids: profile.member_ids,
            warm_account_ids: profile.accounts.filter((item) => item.temperature === 'warm').map((item) => item.id),
            hot_account_ids: profile.accounts.filter((item) => item.temperature === 'hot').map((item) => item.id),
        })
        setModalOpen(true)
    }

    const save = async () => {
        const values = await form.validateFields()
        const payload: VisibilityProfilePayload = {
            name: values.name.trim(),
            wiseway_email: values.wiseway_email.trim(),
            description: values.description?.trim() || null,
            is_active: values.is_active,
            member_ids: values.member_ids || [],
            account_ids: [...(values.warm_account_ids || []), ...(values.hot_account_ids || [])],
        }
        setSaving(true)
        try {
            if (editing) {
                await visibilityProfilesApi.update(editing.id, payload)
                message.success('Профиль сохранён')
            } else {
                await visibilityProfilesApi.create(payload)
                message.success('Профиль создан')
            }
            setModalOpen(false)
            await load()
            onChanged?.()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const remove = async (profile: VisibilityProfile) => {
        try {
            await visibilityProfilesApi.remove(profile.id)
            message.success('Профиль удалён')
            await load()
            onChanged?.()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Space size={12} align="start">
                    <ConfirmSwitch checked={enabled} what="Видимость лидов по профилям" onConfirm={toggleEnabled} />
                    <div>
                        <div>Видимость лидов по профилям</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Лид принадлежит профилю — команде. Сотрудник видит лидов своих профилей (их карточки,
                            диалоги, задачи), своих личных и ничьих без профиля; вести лида может любой из команды.
                            Касается только ролей без права «Видеть все карточки лидов» (ЛМ, Junior LM и подобных):
                            руководители и остальные права работают как раньше. Выключено — всё как до профилей:
                            свой лид — тот, что назначен на сотрудника. Почта для CRM Wiseway берётся из профиля
                            при любом положении переключателя.
                        </Text>
                    </div>
                </Space>

                <div>
                    <Space style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }} wrap>
                        <Text strong>Профили</Text>
                        <Button type="primary" onClick={openCreate}>Новый профиль</Button>
                    </Space>
                    <Table<VisibilityProfile>
                        rowKey="id"
                        dataSource={profiles}
                        pagination={false}
                        size="middle"
                        scroll={{ x: 900 }}
                        locale={{ emptyText: 'Профилей пока нет — создайте первый' }}
                        columns={[
                            { title: 'Название', dataIndex: 'name', key: 'name' },
                            { title: 'Почта Wiseway', dataIndex: 'wiseway_email', key: 'email' },
                            {
                                title: 'TG-аккаунты',
                                key: 'accounts',
                                render: (_, profile) => profile.accounts.length
                                    ? profile.accounts.map((account) => (
                                        <Tag
                                            key={account.id}
                                            color={TEMPERATURE_COLOR[account.temperature]}
                                            title={account.temperature === 'hot' ? 'Горячий пул' : 'Тёплый пул'}
                                        >
                                            {account.label}
                                        </Tag>
                                    ))
                                    : <Text type="secondary">—</Text>,
                            },
                            {
                                title: 'Сотрудники',
                                key: 'members',
                                render: (_, profile) => profile.member_names.length
                                    ? profile.member_names.map((name) => <Tag key={name}>{name}</Tag>)
                                    : <Text type="secondary">никого</Text>,
                            },
                            { title: 'Лидов', dataIndex: 'lead_count', key: 'leads', width: 80 },
                            {
                                title: 'Статус',
                                key: 'active',
                                width: 110,
                                render: (_, profile) => profile.is_active
                                    ? <Tag color="green">Активен</Tag>
                                    : <Tag>Отключён</Tag>,
                            },
                            {
                                title: '',
                                key: 'actions',
                                width: 190,
                                render: (_, profile) => (
                                    <Space>
                                        <Button size="small" onClick={() => openEdit(profile)}>Изменить</Button>
                                        <Popconfirm
                                            title="Удалить профиль?"
                                            description={profile.lead_count
                                                ? 'В профиле есть лиды — удалить не получится, его можно только отключить'
                                                : 'Сотрудники потеряют этот профиль'}
                                            okText="Удалить"
                                            cancelText="Отмена"
                                            onConfirm={() => remove(profile)}
                                        >
                                            <Button size="small">Удалить</Button>
                                        </Popconfirm>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                </div>
            </Space>

            <Modal
                title={editing ? 'Профиль видимости' : 'Новый профиль видимости'}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={save}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={saving}
                destroyOnHidden
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Укажите название' }]}>
                        <Input maxLength={100} placeholder="Например: Индия — команда Анисы" />
                    </Form.Item>
                    <Form.Item
                        name="wiseway_email"
                        label="Почта Wiseway"
                        extra="От этой почты команда пишет в CRM Wiseway. В Wiseway должен быть пользователь с такой почтой."
                        rules={[
                            { required: true, message: 'Укажите почту' },
                            { type: 'email', message: 'Это не похоже на почту' },
                        ]}
                    >
                        <Input placeholder="name@wise-way.tech" />
                    </Form.Item>
                    <Form.Item
                        name="member_ids"
                        label="Сотрудники"
                        extra="Сотрудник профиля сразу видит его лидов. При сохранении лиды сотрудников, у которых ещё нет профиля, переходят в этот профиль."
                    >
                        <Select
                            mode="multiple"
                            options={operatorOptions}
                            optionFilterProp="label"
                            placeholder="Кто работает в этом профиле"
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item
                        name="warm_account_ids"
                        label="Тёплые аккаунты"
                        extra="Аккаунты тёплого пула. Если лид сам написал на такой аккаунт, переписку видят только сотрудники профиля и админы. Если первыми написали мы, диалог виден всем, как обычно."
                    >
                        <Select
                            mode="multiple"
                            options={accountOptions('warm')}
                            optionFilterProp="label"
                            placeholder="Выберите тёплые аккаунты"
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item
                        name="hot_account_ids"
                        label="Горячие аккаунты"
                        extra="Аккаунты горячего пула, то же правило. Один аккаунт — в одном профиле. Рассылка от этого не меняется."
                    >
                        <Select
                            mode="multiple"
                            options={accountOptions('hot')}
                            optionFilterProp="label"
                            placeholder="Выберите горячие аккаунты"
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item name="description" label="Описание">
                        <Input.TextArea rows={2} maxLength={2000} />
                    </Form.Item>
                    <Form.Item name="is_active" label="Активен" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </Spin>
    )
}
